/** @format */

/* eslint @typescript-eslint/await-thenable: "warn" */
import isPropValid from '@emotion/is-prop-valid';
import {
    Button,
    ButtonSize,
    ButtonVariant,
} from '@mpb-com/feather/components/atoms/button';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { ImageWithFallback } from '@mpb-com/feather/components/atoms/image-with-fallback';
import { Notification } from '@mpb-com/feather/components/atoms/notification';
import { NotificationVariant } from '@mpb-com/feather/components/atoms/notification/types';
import { animated, useTransition } from '@react-spring/web';
import { Trans, useTranslation } from 'next-i18next';
import NextLink from 'next/link';
import Router from 'next/router';
import React, {
    Fragment,
    FunctionComponent,
    ReactNode,
    Ref,
    RefObject,
    useContext,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import CloseIcon from '@toucan/client/global/icons/close.svg';

import { useFocusTrap } from '@toucan/client/global/application';
import { formatAsCurrency } from '@toucan/client/global/currency';
import { MarketContext } from '@toucan/client/global/market';
import { fallbackImagePath } from '@toucan/client/global/product';
import { UserContext } from '@toucan/client/global/user';

import {
    BasketItem,
    BasketItemAvailability,
    findAnotherProduct,
    navigateToCheckout,
} from '@toucan/client/core/basket';
import { isScrollAtBottom } from '@toucan/client/core/scroll';

import { BasketContext } from '@toucan/client/shared/context/basket';
import { useClickOutside } from '@toucan/client/shared/hooks/use-click-outside';
import { useKeyPress } from '@toucan/client/shared/hooks/use-key-press';
import { useScroll } from '@toucan/client/shared/hooks/use-scroll';
import { useWindowResize } from '@toucan/client/shared/hooks/use-window-resize';

import { BasketEmptyMessage } from '../basket-empty-message';
import { BasketItem as BasketItemComponent } from '../basket-item';
import { BasketItemProps } from '../basket-item/types';
import { OutOfStockNotification } from '../out-of-stock-notification';
import { styles } from './popout-mini-basket.styles';

export type PopoutMiniBasketProps = {
    /**
     * If the component is open
     */
    isOpen: boolean;

    /**
     * A function used to generate a trigger element
     *
     * @param id {string} The id to assign to a aria-controls attribute
     * @param triggerRef {Ref<HTMLElement>} A ref prop to assign to the component
     */
    trigger: (id: string, triggerRef: Ref<HTMLElement>) => ReactNode;

    /**
     * Handler for when the component should close
     */
    onClose?: () => void;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
};

export type StyledPopoutMiniBasketProps = PopoutMiniBasketProps & {
    /**
     * The height of the window in pixels
     */
    windowHeight: number;

    /**
     * The height of the mini basket header in pixels
     */
    headerHeight: number;

    /**
     * The height of the line items container, including content not visible on the screen due to overflow.
     */
    lineItemsScrollHeight: number;

    /**
     * The height of the line items (including any overflowing content)
     */
    lineItemsHeight: number;

    /**
     * The maximum height that the mini basket can be
     */
    availableBasketHeight: number;

    /**
     * Is the basket notification error visible
     */
    isErrorNotificationVisible: boolean;

    /**
     * Is true when an overflowing line items element is scrolled to the bottom
     */
    lineItemsAtScrollEnd: boolean;

    /**
     * The number of line items in the basket
     */
    numLineItems: number;
};

const StyledPopoutMiniBasket = styled('aside').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

/**
 * Returns the clientHeight from the given ref HTML Element
 *
 * @param {RefObject<HTMLElement>} ref the ref to get the client height from
 * @returns {number} The height of the element in pixels. Returns 0 when no
 * height property can be found
 */
function getClientHeightFromRef(ref: RefObject<HTMLElement>): number {
    if (ref?.current?.clientHeight) {
        return ref?.current?.clientHeight;
    }
    return 0;
}

/**
 * Returns the scrollHeight from the given ref HTML Element
 *
 * @param {RefObject<HTMLElement>} ref the ref to get the scroll height from
 * @returns {number} The scroll height of the element in pixels. Returns 0 when no
 * height property can be found
 */
function getScrollHeightFromRef(ref: RefObject<HTMLElement>): number {
    if (ref?.current?.scrollHeight) {
        return ref?.current?.scrollHeight;
    }
    return 0;
}

export const PopoutMiniBasket: FunctionComponent<PopoutMiniBasketProps> = ({
    isOpen,
    onClose,
    trigger,
    ...otherProps
}: PopoutMiniBasketProps) => {
    const { t } = useTranslation();
    const PopoutMiniBasketWrapperId = 'miniBasketWrapper';

    /**
     * Store whether the summary group is scrolled to the bottom
     * of it's contents
     */
    const [lineItemsAtScrollEnd, setLineItemsAtScrollEnd] =
        useState<boolean>(false);

    const triggerRef = useRef<HTMLElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const lineItemsRef = useRef<HTMLUListElement>(null);
    const summaryRef = useRef<HTMLDivElement>(null);

    const [, windowHeight] = useWindowResize();

    const {
        basketModelSlugs,
        basket,
        getBasket,
        isBasketValid,
        removeUnavailableBasketItems,
    } = useContext(BasketContext);
    const [isErrorNotificationVisible, setIsErrorNotificationVisible] =
        useState<boolean>(false);
    const [basketIsLoading, setBasketIsLoading] = useState<boolean>(false);

    const { activeMarket } = useContext(MarketContext);
    const { isLoggedIn } = useContext(UserContext);

    const formatWithCurrency = formatAsCurrency(
        activeMarket.iso,
        activeMarket.currency
    );

    const headerHeight = getClientHeightFromRef(headerRef);
    const summaryHeight = getClientHeightFromRef(summaryRef);
    const lineItemsScrollHeight = getScrollHeightFromRef(lineItemsRef);

    /*
     *  Set a boolean to denote when an overflowing summary group has scrolled to the
     * end of its container.
     */
    useScroll(lineItemsRef, () => {
        setLineItemsAtScrollEnd(isScrollAtBottom(lineItemsRef));
    });

    /*
     * 96px = the height of the website header
     */
    const websiteHeaderHeight = 96;

    /*
     * Define a margin so a 'tall' basket doesn't touch the bottom of the screen
     * 24px = var(--layout-spacing-24). A gap to ensure we never touch the bottom
     * of the page.
     */
    const availableHeightMargin = 24;

    // Calculate the maximum height that the mini basket can be
    const availableBasketHeight =
        windowHeight - websiteHeaderHeight - availableHeightMargin;

    // Calculate the height that the mini basket line items container should be
    const lineItemsHeight =
        availableBasketHeight - headerHeight - summaryHeight;

    /**
     * Check whether the basket is in a valid state, then navigate to the
     * checkout or show an error message
     */
    async function continueToCheckout() {
        setIsErrorNotificationVisible(false);
        setBasketIsLoading(true);
        await getBasket();
        const isValid = isBasketValid();

        // if the form is not valid then we show an error message
        if (!isValid && !isErrorNotificationVisible) {
            setIsErrorNotificationVisible(true);
            setBasketIsLoading(false);
            return;
        }

        // if the form is valid we can continue or
        // if the form is not valid and the error message is showing then we
        // continue but we need to remove any invalid items
        if (isErrorNotificationVisible) {
            await removeUnavailableBasketItems();
        }
        navigateToCheckout(activeMarket.slug, isLoggedIn, t, Router);
    }

    const { availableSummaryItems, allocatedSummaryItems, summaryItemsTotal } =
        basket.items.reduce(
            (
                {
                    availableSummaryItems,
                    allocatedSummaryItems,
                    summaryItemsTotal,
                }: any,
                item: BasketItem
            ) => {
                if (item.availability === BasketItemAvailability.AVAILABLE) {
                    availableSummaryItems.push({
                        title: item.name,
                        price: formatWithCurrency(parseFloat(item.listPrice)),
                        condition: item.condition,
                        sku: item.sku,
                        image: (
                            <ImageWithFallback
                                src={item.imageUrl}
                                fallbackSrc={fallbackImagePath}
                                alt={item.name}
                            />
                        ),
                    });

                    summaryItemsTotal += parseFloat(item.listPrice);
                } else {
                    allocatedSummaryItems.push({
                        title: item.name,
                        price: formatWithCurrency(parseFloat(item.listPrice)),
                        condition: item.condition,
                        sku: item.sku,
                        image: (
                            <ImageWithFallback
                                src={item.imageUrl}
                                fallbackSrc={fallbackImagePath}
                                alt={item.name}
                            />
                        ),
                        disabled: true,
                        children: (
                            <OutOfStockNotification
                                handleReplace={() =>
                                    findAnotherProduct(
                                        activeMarket.slug,
                                        basketModelSlugs[item.modelId],
                                        t,
                                        Router
                                    )
                                }
                            />
                        ),
                    });
                }

                return {
                    availableSummaryItems,
                    allocatedSummaryItems,
                    summaryItemsTotal,
                };
            },
            {
                availableSummaryItems: [],
                allocatedSummaryItems: [],
                summaryItemsTotal: 0,
            }
        );

    const formattedSummaryItemsTotal = formatWithCurrency(summaryItemsTotal);

    // Close the basket when click outside
    useClickOutside([wrapperRef, triggerRef], () => {
        onClose && onClose();
    });

    useFocusTrap(wrapperRef, [isOpen]);

    // Close the basket when Escape is pressed
    useKeyPress(
        wrapperRef.current,
        ['Escape', 'Esc'],
        (event: KeyboardEvent) => {
            onClose && onClose();
        }
    );

    const menuWrapperTransitions = useTransition(isOpen, {
        from: {
            opacity: 0,
        },
        enter: {
            opacity: 1,
        },
        leave: {
            opacity: 0,
        },
    });

    const getBasketItems = (
        allocatedItems: BasketItemProps[],
        availableItems: BasketItemProps[]
    ) => {
        const items = [...allocatedItems, ...availableItems];

        return items.map((props) => {
            return <BasketItemComponent key={props.sku} {...props} />;
        });
    };

    return (
        <StyledPopoutMiniBasket
            onClose={onClose}
            windowHeight={windowHeight}
            numLineItems={
                availableSummaryItems.length + allocatedSummaryItems.length
            }
            lineItemsHeight={lineItemsHeight}
            lineItemsScrollHeight={lineItemsScrollHeight}
            availableBasketHeight={availableBasketHeight}
            isErrorNotificationVisible={isErrorNotificationVisible}
            headerHeight={headerHeight}
            lineItemsAtScrollEnd={lineItemsAtScrollEnd}
            isOpen={isOpen}
            trigger={trigger}
            {...otherProps}
        >
            {trigger(PopoutMiniBasketWrapperId, triggerRef)}
            {menuWrapperTransitions((props, item) =>
                item ? (
                    <animated.div
                        className="mini-basket-wrapper"
                        style={props}
                        ref={wrapperRef}
                    >
                        <div className="mini-basket">
                            <div className="mini-basket-header" ref={headerRef}>
                                <Heading
                                    size={HeadingSize.H3}
                                    className="mini-basket-title"
                                >
                                    {t('Your basket')}
                                </Heading>
                                <Icon
                                    title={t('Close')}
                                    variant={IconVariant.NAVIGATION}
                                    size={IconSize.S}
                                    onClick={() => onClose?.()}
                                    className="mini-basket-close-button"
                                    data-testid="mini-basket__close-button"
                                >
                                    <CloseIcon />
                                </Icon>
                            </div>

                            {availableSummaryItems.length +
                                allocatedSummaryItems.length ===
                            0 ? (
                                <BasketEmptyMessage />
                            ) : (
                                <Fragment>
                                    <div className="mini-basket-line-items">
                                        <ul>
                                            {getBasketItems(
                                                availableSummaryItems,
                                                allocatedSummaryItems
                                            )}
                                        </ul>
                                    </div>
                                    <div
                                        className="mini-basket-summary"
                                        ref={summaryRef}
                                    >
                                        {isErrorNotificationVisible && (
                                            <Notification
                                                variant={
                                                    NotificationVariant.ERROR
                                                }
                                                className="basket-error-notification"
                                                iconSize={IconSize.L}
                                            >
                                                <Trans>
                                                    {t(
                                                        '<strong>MPB stock moves fast — and so do our customers.</strong><br />Sorry, one or more items in your basket are no longer available.'
                                                    )}
                                                </Trans>
                                            </Notification>
                                        )}
                                        <div className="mini-basket-summary-content">
                                            <p
                                                className="mini-basket-summary-total"
                                                data-testid="mini-basket__summary_total"
                                            >
                                                <span>{t('Total')}</span>
                                                {formattedSummaryItemsTotal}
                                            </p>
                                            <div className="mini-basket-summary-cta">
                                                <NextLink
                                                    prefetch={false}
                                                    href="/basket"
                                                    as={`/${
                                                        activeMarket.slug
                                                    }/s/${t('basket', {
                                                        ns: 'urls',
                                                    })}`}
                                                    passHref={true}
                                                    legacyBehavior
                                                >
                                                    <Button
                                                        variant={
                                                            ButtonVariant.TERTIARY
                                                        }
                                                        size={ButtonSize.SMALL}
                                                        href="/basket"
                                                        data-testid="mini-basket__edit-basket-button"
                                                    >
                                                        {t('Edit basket')}
                                                    </Button>
                                                </NextLink>

                                                <Button
                                                    variant={
                                                        ButtonVariant.PRIMARY
                                                    }
                                                    size={ButtonSize.SMALL}
                                                    data-testid="mini-basket__checkout-button"
                                                    onClick={continueToCheckout}
                                                    isLoading={basketIsLoading}
                                                    disabled={
                                                        availableSummaryItems.length ===
                                                        0
                                                    }
                                                >
                                                    {t('Checkout now')}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Fragment>
                            )}
                        </div>
                    </animated.div>
                ) : null
            )}
        </StyledPopoutMiniBasket>
    );
};
